import React from "react";
import { useNavigate } from "react-router-dom";
import { useFetch } from "@/lib/api";
import { Loading, SectionTitle } from "@/components/common";
import { GitBranch } from "lucide-react";

const CONF_COLOR = {
  Confirmed: "#10b981",
  "Strongly supported": "#e6a15c",
  Possible: "#06b6d4",
  Unknown: "#64748b",
};
const NODE_STYLE = {
  wallet: { fill: "#78350f", stroke: "#e6a15c", text: "#fde68a" },
  funding: { fill: "#0e2a3a", stroke: "#06b6d4", text: "#a5f3fc" },
  related: { fill: "#1e293b", stroke: "#64748b", text: "#cbd5e1" },
  token: { fill: "#052e2b", stroke: "#10b981", text: "#6ee7b7" },
};

export default function WalletRelationshipGraph({ address }) {
  const { data, loading } = useFetch(`/wallets/${address}/graph`, [address]);
  const nav = useNavigate();

  if (loading || !data) return <div className="pnut-card p-5"><Loading label="Building relationship graph…" /></div>;
  const { nodes, edges, cluster_size } = data.data;

  const W = 620, H = 420, cx = W / 2, cy = H / 2;
  const center = nodes.find((n) => n.central) || nodes[0];
  const others = nodes.filter((n) => n !== center);
  const pos = { [center.id]: { x: cx, y: cy } };
  others.forEach((n, i) => {
    const angle = (2 * Math.PI * i) / others.length - Math.PI / 2;
    const radius = n.type === "funding" ? 120 : 170;
    pos[n.id] = { x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) };
  });

  const nodeR = (n) => (n.central ? 34 : n.type === "token" ? 26 : 24);

  return (
    <div className="pnut-card p-5" data-testid="wallet-graph">
      <SectionTitle right={
        <span className="rounded border border-slate-800 px-2 py-0.5 font-mono text-[10px] text-slate-400">
          cluster overlap: {cluster_size}
        </span>
      }>
        <span className="inline-flex items-center gap-1.5"><GitBranch className="h-4 w-4 text-amber-400" /> Funding & Cluster Graph</span>
      </SectionTitle>

      <div className="mb-3 flex flex-wrap gap-3">
        {Object.entries(CONF_COLOR).map(([k, c]) => (
          <span key={k} className="inline-flex items-center gap-1.5 text-[10px] text-slate-400">
            <span className="inline-block h-0.5 w-4 rounded" style={{ background: c }} /> {k}
          </span>
        ))}
      </div>

      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 520 }}>
          {edges.map((e, i) => {
            const a = pos[e.from], b = pos[e.to];
            if (!a || !b) return null;
            const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
            return (
              <g key={i}>
                <line x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                  stroke={CONF_COLOR[e.confidence]} strokeWidth={e.confidence === "Confirmed" ? 2 : 1.3}
                  strokeDasharray={e.confidence === "Possible" || e.confidence === "Unknown" ? "4 4" : ""}
                  opacity={0.7} />
                <text x={mx} y={my - 3} fill="#64748b" fontSize="8" textAnchor="middle" className="font-mono">{e.relation}</text>
              </g>
            );
          })}
          {nodes.map((n) => {
            const p = pos[n.id];
            if (!p) return null;
            const st = NODE_STYLE[n.type] || NODE_STYLE.related;
            const clickable = n.type === "token" ? `/token/${n.ref}` : (n.type === "related" || n.central) ? null : null;
            return (
              <g key={n.id} style={{ cursor: clickable ? "pointer" : "default" }}
                data-testid={n.type === "token" ? `wallet-graph-token-${n.ref}` : `wallet-graph-node-${n.type}`}
                onClick={() => clickable && nav(clickable)}>
                <circle cx={p.x} cy={p.y} r={nodeR(n)} fill={st.fill} stroke={st.stroke}
                  strokeWidth={n.central ? 2.5 : 1.5} />
                <text x={p.x} y={p.y - 1} fill={st.text} fontSize="9" fontWeight="600" textAnchor="middle" className="font-mono">{n.short}</text>
                <text x={p.x} y={p.y + 10} fill="#94a3b8" fontSize="7" textAnchor="middle">{n.label}</text>
              </g>
            );
          })}
        </svg>
      </div>
      <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
        Relationships are labeled by confidence — <span className="text-emerald-400">Confirmed</span> (on-chain holdings),
        <span className="text-amber-400"> Strongly supported</span>, <span className="text-cyan-400">Possible</span>, and
        <span className="text-slate-400"> Unknown</span>. Speculation is never presented as fact. Data is DEMO.
      </p>
    </div>
  );
}
